import Autocomplete from '@mui/material/Autocomplete'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import { useMutation, useQuery } from '@tanstack/react-query'
import { KNTrailerIcon, KNTruckIcon } from 'components/KNIcon/KNIcon'
import { getRouteName } from 'global/helpers/activeRoute'
import { analyticsEvent } from 'global/helpers/analytics'
import i18n from 'i18n'
import { type ReactElement, type SyntheticEvent, useCallback, useMemo } from 'react'
import TrackingLabel from 'screens/VehicleManager/TrackingLabel'
import { vehiclesQueryOptions } from 'screens/VehicleManager/VehicleManager.service'
import { type VehicleData, VehicleType } from 'screens/VehicleManager/VehicleManager.types'
import { assignVehicle } from './TripDashboard.service'
import type { AssignVehicleFormValues, TripData } from './TripDashboard.types'

export interface VehicleAssignmentFieldProps {
  trip: TripData,
  vehicleType?: VehicleType,
  onChange?: (updatedTrip: TripData) => void
}

const getIcon = (type: VehicleType) => {
  switch (type) {
    case VehicleType.TRAILER:
      return <KNTrailerIcon sx={{ color: 'primary.light', width: '16px', height: '16px' }} />
    case VehicleType.TRUCK:
    default:
      return <KNTruckIcon sx={{ color: 'primary.light', width: '16px', height: '16px' }} />
  }
}

const getPlaceholder = (type?: VehicleType): string => {
  switch (type) {
    case VehicleType.TRUCK:
      return i18n.t('screens.cs.trip_dashboard.card.actions.select_truck')
    case VehicleType.TRAILER:
      return i18n.t('screens.cs.trip_dashboard.card.actions.select_trailer')
    default:
      return i18n.t('screens.cs.trip_dashboard.card.actions.select_vehicle')
  }
}

const VehicleAssignmentField = ({
  trip,
  vehicleType,
  onChange,
}: VehicleAssignmentFieldProps) => {
  const vehiclesQuery = useQuery(vehiclesQueryOptions)
  const assignVehicleMutation = useMutation({
    mutationFn: (data: AssignVehicleFormValues) => assignVehicle(trip.entityId, data),
  })
  const options = useMemo(() => {
    if (!vehiclesQuery.isSuccess) {
      return []
    }
    return vehiclesQuery.data.filter((vehicle) => !vehicleType || vehicle.vehicleType === vehicleType)
  }, [vehiclesQuery, vehicleType])

  const getValue = useCallback(() => {
    if (vehicleType === VehicleType.TRUCK) {
      return trip.assignedTruck
    } else if (vehicleType === VehicleType.TRAILER) {
      return trip.assignedTrailer
    }
    return undefined
  }, [vehicleType, trip.assignedTruck, trip.assignedTrailer])

  const handleChange = useCallback(async (_event: SyntheticEvent, newValue: VehicleData): Promise<void> => {
    await assignVehicleMutation.mutateAsync({
      licensePlate: newValue.licensePlate,
      vehicleType: newValue.vehicleType,
      isTrackable: newValue.telematicsReady,
    })
    if (newValue.vehicleType === VehicleType.TRUCK) {
      onChange?.({
        ...trip,
        assignedTruckIdentifier: newValue.licensePlate,
        assignedTruck: newValue,
      })
    } else if (newValue.vehicleType === VehicleType.TRAILER) {
      onChange?.({
        ...trip,
        assignedTrailerIdentifier: newValue.licensePlate,
        assignedTrailer: newValue,
      })
    }
    analyticsEvent('polestar_cs_vehicle_assigned', [getRouteName(location.pathname)])
  }, [onChange, trip])

  return (
    <Autocomplete
      options={options}
      disableClearable
      disableListWrap
      fullWidth
      size="small"
      getOptionKey={(vehicle) => vehicle.id}
      getOptionLabel={(vehicle) => vehicle.licensePlate}
      value={getValue()}
      onChange={handleChange}
      renderOption={(props, vehicle) => (
        <li {...props} key={vehicle.id}>
          <Stack spacing={1} direction="row" alignItems="center">
            {getIcon(vehicle.vehicleType)}
            <TrackingLabel vehicle={vehicle} compact />
            <span>{vehicle.licensePlate}</span>
          </Stack>
        </li>
      )}
      renderInput={(params): ReactElement => (
        <TextField {...params} placeholder={getPlaceholder(vehicleType)} />
      )}
      isOptionEqualToValue={(option, value): boolean => option.id === value.id}
      slotProps={{
        popper: {
          placement: 'bottom-start',
          sx: { width: 'auto !important' },
        },
      }}
    />
  )
}

export default VehicleAssignmentField
